import React, { useState, useEffect, useCallback } from "react";
import { useApp } from "../../context/AppContext";
import * as api from "../../lib/api";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { EmptyState } from "../shared/EmptyState";
import { toast } from "sonner";
import { Users, UserPlus, UserMinus, ArrowRightLeft, Clock, CheckCircle, XCircle, ChevronDown, ChevronUp, Phone, Mail, Briefcase } from "lucide-react";

const TYPE_CONFIG = {
    add_member: { label: "Ajout", icon: UserPlus, color: "bg-emerald-100 text-emerald-700" },
    remove_member: { label: "Retrait", icon: UserMinus, color: "bg-red-100 text-red-700" },
    transfer_member: { label: "Transfert", icon: ArrowRightLeft, color: "bg-blue-100 text-blue-700" },
};

const STATUS_CONFIG = {
    pending: { label: "En attente", color: "bg-amber-100 text-amber-700", icon: Clock },
    approved: { label: "Approuvée", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
    rejected: { label: "Refusée", color: "bg-red-100 text-red-700", icon: XCircle },
};

export default function AdminTeamRequests() {
    const { hasPermission } = useApp();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("pending");
    const [expandedId, setExpandedId] = useState(null);
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectId, setRejectId] = useState(null);
    const [rejectReason, setRejectReason] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const loadRequests = useCallback(async () => {
        try {
            setLoading(true);
            const params = {};
            if (statusFilter) params.status = statusFilter;
            const data = await api.getTeamRequests(params);
            setRequests(data);
        } catch {
            toast.error("Erreur de chargement");
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => { loadRequests(); }, [loadRequests]);

    const handleApprove = async (reqId) => {
        setSubmitting(true);
        try {
            await api.approveTeamRequest(reqId);
            toast.success("Demande approuvée. Le changement a été appliqué.");
            loadRequests();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur");
        } finally {
            setSubmitting(false);
        }
    };

    const handleReject = async () => {
        setSubmitting(true);
        try {
            await api.rejectTeamRequest(rejectId, { reason: rejectReason });
            toast.success("Demande refusée");
            setRejectOpen(false);
            setRejectId(null);
            setRejectReason("");
            loadRequests();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur");
        } finally {
            setSubmitting(false);
        }
    };

    if (!hasPermission("admin")) return null;

    const filters = [
        { id: "pending", label: "En attente" },
        { id: "approved", label: "Approuvées" },
        { id: "rejected", label: "Refusées" },
        { id: "", label: "Toutes" },
    ];

    const formatDate = (d) => {
        if (!d) return "";
        try { return new Date(d).toLocaleDateString("fr-CH", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
        catch { return d; }
    };

    return (
        <div className="page-container" data-testid="admin-team-requests">
            <div className="page-header">
                <h1 className="page-title">{"Demandes d'équipe"}</h1>
                <p className="page-subtitle">{requests.length} demande{requests.length !== 1 ? "s" : ""}</p>
            </div>

            {/* Status filter tabs */}
            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit mb-6" data-testid="status-filter">
                {filters.map((f) => (
                    <button key={f.id} onClick={() => setStatusFilter(f.id)} className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${statusFilter === f.id ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`} data-testid={`filter-${f.id || "all"}`}>
                        {f.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="animate-pulse text-gray-400">Chargement...</div>
            ) : requests.length === 0 ? (
                <EmptyState icon={Users} title="Aucune demande" description={statusFilter === "pending" ? "Aucune demande en attente." : "Aucune demande trouvée."} />
            ) : (
                <div className="card">
                    <div className="card-body p-0">
                        <div className="divide-y divide-gray-100">
                            {requests.map((req) => {
                                const tc = TYPE_CONFIG[req.type] || {};
                                const sc = STATUS_CONFIG[req.status] || {};
                                const TypeIcon = tc.icon || Clock;
                                const StatusIcon = sc.icon || Clock;
                                const isExpanded = expandedId === req.id;
                                return (
                                    <div key={req.id} data-testid={`admin-request-row-${req.id}`}>
                                        <div className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : req.id)}>
                                            <span className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${tc.color} flex-shrink-0`}>
                                                <TypeIcon className="w-3 h-3" /> {tc.label}
                                            </span>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 truncate">
                                                    {req.target_user_name || "Nouveau contact"}
                                                </p>
                                                <p className="text-xs text-gray-500">{req.department_name}{req.type === "transfer_member" && req.to_department_name ? ` \u2192 ${req.to_department_name}` : ""}</p>
                                            </div>
                                            <div className="text-xs text-gray-400 flex-shrink-0">{req.requested_by_name}</div>
                                            <div className="text-xs text-gray-400 flex-shrink-0 w-28 text-right">{formatDate(req.requested_at)}</div>
                                            <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${sc.color} flex-shrink-0`}>
                                                <StatusIcon className="w-3 h-3" /> {sc.label}
                                            </span>
                                            {req.status === "pending" && (
                                                <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                                    <Button size="sm" className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700" onClick={() => handleApprove(req.id)} disabled={submitting} data-testid={`approve-btn-${req.id}`}>
                                                        <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approuver
                                                    </Button>
                                                    <Button size="sm" variant="outline" className="h-7 px-2 text-red-600 border-red-200 hover:bg-red-50" onClick={() => { setRejectId(req.id); setRejectReason(""); setRejectOpen(true); }} disabled={submitting} data-testid={`reject-btn-${req.id}`}>
                                                        <XCircle className="w-3.5 h-3.5 mr-1" /> Refuser
                                                    </Button>
                                                </div>
                                            )}
                                            {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                                        </div>
                                        {isExpanded && (
                                            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 text-sm space-y-2" data-testid={`request-detail-${req.id}`}>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div><span className="text-gray-500">Type :</span> <span className="font-medium">{tc.label}</span></div>
                                                    <div><span className="text-gray-500">{"Département :"}</span> <span className="font-medium">{req.department_name}</span></div>
                                                    <div><span className="text-gray-500">Collaborateur :</span> <span className="font-medium">{req.target_user_name || "Nouveau contact"}</span></div>
                                                    {req.type === "transfer_member" && (
                                                        <div><span className="text-gray-500">Transfert :</span> <span className="font-medium">{req.from_department_name || "N/A"} → {req.to_department_name || "N/A"}</span></div>
                                                    )}
                                                    {req.new_user_data && (
                                                        <>
                                                            {req.new_user_data.phone_mobile && <div className="flex items-center gap-1"><Phone className="w-3 h-3 text-gray-400" /> {req.new_user_data.phone_mobile}</div>}
                                                            {req.new_user_data.email && <div className="flex items-center gap-1"><Mail className="w-3 h-3 text-gray-400" /> {req.new_user_data.email}</div>}
                                                        </>
                                                    )}
                                                    {req.reason && <div className="col-span-2"><span className="text-gray-500">Motif :</span> <span>{req.reason}</span></div>}
                                                    <div><span className="text-gray-500">{"Demandé par :"}</span> <span className="font-medium">{req.requested_by_name}</span>{" le "}{formatDate(req.requested_at)}</div>
                                                    {req.decided_by_name && (
                                                        <div><span className="text-gray-500">{"Traité par :"}</span> <span className="font-medium">{req.decided_by_name}</span>{" le "}{formatDate(req.decided_at)}</div>
                                                    )}
                                                    {req.status === "rejected" && req.rejection_reason && (
                                                        <div className="col-span-2 p-2 bg-red-50 rounded-md border border-red-200 text-red-700">
                                                            <span className="font-medium">Motif du refus :</span> {req.rejection_reason}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Reject dialog */}
            <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
                <DialogContent data-testid="reject-dialog">
                    <DialogHeader>
                        <DialogTitle>Refuser la demande</DialogTitle>
                    </DialogHeader>
                    <div>
                        <label className="form-label text-xs">Motif du refus (optionnel)</label>
                        <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} className="w-full px-3 py-2 text-sm border rounded-md resize-none" rows={3} placeholder={"Expliquez pourquoi cette demande est refusée..."} data-testid="reject-reason-input" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRejectOpen(false)}>Annuler</Button>
                        <Button variant="destructive" onClick={handleReject} disabled={submitting} data-testid="submit-reject">
                            {submitting ? "Envoi..." : "Refuser"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
